import {
  BoundNumberField,
  column,
  GridColumn,
  GridDataRow,
  GridTable,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import {
  Maybe,
  SaveUnderwritingConfigMetroInput,
  UnderwritingOverviewPageUnderwritingConfigFragment,
  UnderwritingOverviewPageUnderwritingConfigMetroFragment,
  useSaveUnderwritingConfigMetroMutation,
} from "src/generated/graphql-types";

export type UnderwritingMetrosTableProps = {
  metros: UnderwritingOverviewPageUnderwritingConfigMetroFragment[];
  underwritingConfig: UnderwritingOverviewPageUnderwritingConfigFragment;
};

export type SaveUnderwritingMetroDetails = SaveUnderwritingConfigMetroInput;

export function UnderwritingMetrosTable(props: UnderwritingMetrosTableProps) {
  const { metros, underwritingConfig } = props;

  const [saveUnderwritingMetroMutation] = useSaveUnderwritingConfigMetroMutation();

  const formConfigMetro: ObjectConfig<FormInput> = useMemo(
    () => ({
      underwritingConfigMetros: {
        type: "list",
        config: underwritingConfigMetroConfig,
      },
    }),
    [],
  );

  const formState = useFormState({
    config: formConfigMetro,
    init: {
      input: underwritingConfig,
      map: mapToMetroForm,
    },
    autoSave: saveUnderwritingMetros,
  });

  async function saveUnderwritingMetros(formState: ObjectState<FormInput>) {
    const changedUnderwritingMetros =
      formState.changedValue.underwritingConfigMetros?.filter(
        (fm) =>
          fm.targetInvestorMarginBasisPoints !== undefined ||
          fm.buildMarginBasisPoints !== undefined ||
          fm.siteAcquisitionMarkupBasisPoints !== undefined ||
          fm.weightedFinancingRate !== undefined ||
          fm.financingRate !== undefined ||
          fm.percentDebtFinancedBasisPoints !== undefined ||
          fm.preConMonths !== undefined ||
          fm.constructionMonths !== undefined ||
          fm.salesPeriodMonths !== undefined ||
          fm.exitRealtorFeeBasisPoints !== undefined ||
          fm.acquisitionClosingCostsBasisPoints !== undefined,
      ) || [];
    await Promise.all(
      changedUnderwritingMetros.map(async (fm) =>
        saveUnderwritingMetroMutation({
          variables: { input: { ...fm } },
        }),
      ),
    );
  }

  return (
    <>
      <GridTable
        id="underwritingConfigMetrosTable"
        rowStyles={rowStyles}
        columns={createUnderwritingMetroColumns(formState)}
        rows={createUnderwritingMetroRows(metros)}
        fallbackMessage="Underwriting Metros for this Underwriting will show here."
      />
    </>
  );
}

type Row = SimpleHeaderAndData<UnderwritingOverviewPageUnderwritingConfigMetroFragment>;

const rowStyles: RowStyles<Row> = {
  header: {},
  data: {},
};

function createUnderwritingMetroRows(
  metros: UnderwritingOverviewPageUnderwritingConfigMetroFragment[],
): GridDataRow<Row>[] {
  return simpleDataRows(metros);
}

function createUnderwritingMetroColumns(formState: ObjectState<FormInput>): GridColumn<Row>[] {
  const underwritingConfigMetroIdColumn = column<Row>({
    header: () => "Id",
    data: ({ id }) => id,
  });

  const metroNameColumn = column<Row>({
    header: () => "Metro",
    data: ({ metro }) => metro.name,
  });

  const targetInvestorMarginColumn = column<Row>({
    header: () => "Target Investor Margin",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.targetInvestorMarginBasisPoints,
        content: () => <BoundNumberField type="basisPoints" field={formStateField!.targetInvestorMarginBasisPoints} />,
      };
    },
  });

  const buildMarginColumn = column<Row>({
    header: () => "Build Margin",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.buildMarginBasisPoints,
        content: () => <BoundNumberField type="basisPoints" field={formStateField!.buildMarginBasisPoints} />,
      };
    },
  });

  const siteAcquisitionMarkupColumn = column<Row>({
    header: () => "Site Acquisition Markup",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.siteAcquisitionMarkupBasisPoints,
        content: () => <BoundNumberField type="basisPoints" field={formStateField!.siteAcquisitionMarkupBasisPoints} />,
      };
    },
  });

  const weightedFinancingRateColumn = column<Row>({
    header: () => "Weighted Financing Rate",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.weightedFinancingRate,
        content: () => <BoundNumberField field={formStateField!.weightedFinancingRate} numFractionDigits={5} />,
      };
    },
  });

  const financingRateColumn = column<Row>({
    header: () => "Financing Rate",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.financingRate,
        content: () => <BoundNumberField field={formStateField!.financingRate} numFractionDigits={5} />,
      };
    },
  });

  const percentDebtFinancedColumn = column<Row>({
    header: () => "Percent Debt Financed",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.percentDebtFinancedBasisPoints,
        content: () => <BoundNumberField type="basisPoints" field={formStateField!.percentDebtFinancedBasisPoints} />,
      };
    },
  });

  const preConMonthsColumn = column<Row>({
    header: () => "Pre-Construction Months",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.preConMonths,
        content: () => <BoundNumberField field={formStateField!.preConMonths} numFractionDigits={2} />,
      };
    },
  });

  const constructionMonthsColumn = column<Row>({
    header: () => "Construction Months",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.constructionMonths,
        content: () => <BoundNumberField field={formStateField!.constructionMonths} numFractionDigits={2} />,
      };
    },
  });

  const salesPeriodMonthsColumn = column<Row>({
    header: () => "Sales Period Months",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.salesPeriodMonths,
        content: () => <BoundNumberField field={formStateField!.salesPeriodMonths} numFractionDigits={2} />,
      };
    },
  });

  const exitRealtorFeeColumn = column<Row>({
    header: () => "Exit Realtor Fee",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.exitRealtorFeeBasisPoints,
        content: () => <BoundNumberField type="basisPoints" field={formStateField!.exitRealtorFeeBasisPoints} />,
      };
    },
  });

  const acquisitionClosingCostsColumn = column<Row>({
    header: () => "Acquisition Closing Costs",
    data: (row) => {
      const formStateField = formState.underwritingConfigMetros.rows.find((fm) => fm.id.value === row.id);
      return {
        value: () => row.acquisitionClosingCostsBasisPoints,
        content: () => (
          <BoundNumberField type="basisPoints" field={formStateField!.acquisitionClosingCostsBasisPoints} />
        ),
      };
    },
  });

  return [
    underwritingConfigMetroIdColumn,
    metroNameColumn,
    targetInvestorMarginColumn,
    buildMarginColumn,
    siteAcquisitionMarkupColumn,
    weightedFinancingRateColumn,
    financingRateColumn,
    percentDebtFinancedColumn,
    preConMonthsColumn,
    constructionMonthsColumn,
    salesPeriodMonthsColumn,
    exitRealtorFeeColumn,
    acquisitionClosingCostsColumn,
  ];
}

function mapToMetroForm(underwritingConfig: UnderwritingOverviewPageUnderwritingConfigFragment): FormInput {
  return {
    id: underwritingConfig.id,
    underwritingConfigMetros: mapUnderwritingMetrosToForm(underwritingConfig.metros),
  };
}

export function mapUnderwritingMetrosToForm(
  underwritingConfigMetros: Maybe<UnderwritingOverviewPageUnderwritingConfigMetroFragment[]>,
) {
  return underwritingConfigMetros?.map((fm) => {
    return {
      ...fm,
    };
  });
}

type FormInput = SaveUnderwritingMetroDetails & {
  underwritingConfigMetros: Maybe<SaveUnderwritingMetroDetails[]>;
};

export const underwritingConfigMetroConfig: ObjectConfig<SaveUnderwritingMetroDetails> = {
  id: { type: "value" },
  targetInvestorMarginBasisPoints: { type: "value", rules: [required] },
  buildMarginBasisPoints: { type: "value", rules: [required] },
  siteAcquisitionMarkupBasisPoints: { type: "value", rules: [required] },
  weightedFinancingRate: { type: "value", rules: [required] },
  financingRate: { type: "value", rules: [required] },
  percentDebtFinancedBasisPoints: { type: "value", rules: [required] },
  preConMonths: { type: "value", rules: [required] },
  constructionMonths: { type: "value", rules: [required] },
  salesPeriodMonths: { type: "value", rules: [required] },
  exitRealtorFeeBasisPoints: { type: "value", rules: [required] },
  acquisitionClosingCostsBasisPoints: { type: "value", rules: [required] },
};
